/**
 * Audit Data Service
 * Provides audit-related API calls with demo/live mode switching
 * Follows the same pattern as data-service.ts
 */

import { getBackendUrl } from '@/hooks/use-get-backend-url';
import { getAuthConfig } from '@/lib/auth-fetch';
import { mockApiService } from '@/services/demo';
import type {
  Audit,
  AuditException,
  AuditActivityLog,
  PagedResult,
  DashboardMetrics,
  AuditAssignee,
} from '@/types/asset-audit';
import {
  AuditStatus,
  AuditType,
  AuditPriority,
  AuditScope,
  AssigneeStatus,
} from '@/types/asset-audit';

// Helper function to parse API error response - extracted to reduce cognitive complexity (S3776)
async function parseAuditApiErrorResponse(response: Response): Promise<string> {
  let errorMessage = `API error: ${response.status} ${response.statusText}`;
  try {
    const errorData = await response.json();
    if (errorData.message) {
      errorMessage = errorData.message;
    }
  } catch {
    // Ignore JSON parse errors
  }
  return errorMessage;
}

// Helper function to get Bearer token for audit API - extracted to reduce cognitive complexity (S3776)
async function getAuditBearerToken(): Promise<string | null> {
  const authConfig = getAuthConfig();
  if (authConfig.getAccessToken) {
    try {
      return await authConfig.getAccessToken();
    } catch (error) {
      console.error('Error getting access token:', error);
    }
  }
  return null;
}

// ==================== Backend DTO Types ====================
// These types represent the shape of data from the backend API

interface BackendCreateAuditRequest {
  Name: string;
  Description?: string;
  Type: string;
  ScopeType: string;
  LocationId?: number;
  LocationIds?: number[];
  IncludeSubLocations?: boolean;
  CategoryId?: number;
  CategoryIds?: number[];
  AssetIds?: string[];
  AssignedToUserId?: number;
  AssignedToUserName?: string;
  StartDate?: string;
  DueDate?: string;
  Priority?: string;
  IsRecurring?: boolean;
  RecurrenceFrequency?: string;
  RecurrenceDaysOfWeek?: number[];
  RecurrenceDayOfMonth?: number;
  RecurrenceEndType?: string;
  RecurrenceEndOccurrences?: number;
  RecurrenceEndDate?: string;
  AutoApprove?: boolean;
  RequirePhotos?: boolean;
  RequirePhotosForDamaged?: boolean;
  MandatoryFields?: string[];
}

interface BackendAssetAuditDto {
  id: number;
  auditId: string;
  name: string;
  description?: string;
  type: string;
  status: string;
  scopeType: string;
  locationId?: number;
  locationIds?: number[];
  includeSubLocations: boolean;
  categoryId?: number;
  categoryIds?: number[];
  assetIds?: string[];
  totalAssets: number;
  assignedToUserId?: number;
  assignedToUserName: string;
  assignedDate: string;
  startDate?: string;
  dueDate?: string;
  priority?: string;
  isRecurring: boolean;
  recurrenceFrequency?: string;
  recurrenceDaysOfWeek?: number[];
  recurrenceDayOfMonth?: number;
  recurrenceEndType?: string;
  recurrenceEndOccurrences?: number;
  recurrenceEndDate?: string;
  recurrenceActive: boolean;
  parentRecurringAuditId?: number;
  autoApprove: boolean;
  requirePhotos: boolean;
  requirePhotosForDamaged: boolean;
  mandatoryFields?: string[];
  assetsAudited: number;
  assetsVerified: number;
  assetsFlagged: number;
  assetsMissing: number;
  progressPercentage: number;
  createdBy: string;
  createdDate: string;
  modifiedBy?: string;
  modifiedDate?: string;
  completedDate?: string;
  approvedBy?: string;
  approvedDate?: string;
  approvalComment?: string;
  events?: BackendAssetAuditEventDto[];
}

interface BackendAssetAuditEventDto {
  id: number;
  auditId: number;
  eventType: string;
  userId: string;
  userName: string;
  timestamp: string;
  details?: string;
  comment?: string;
}

interface BackendAssetAuditResultDto {
  id: number;
  auditId: number;
  assetCode: string;
  assetName: string;
  status: string;
  isVerified: boolean;
  isFlagged: boolean;
  isMissing: boolean;
  expectedLocationId?: string;
  expectedLocationName?: string;
  actualLocationId?: string;
  actualLocationName?: string;
  locationVerified: boolean;
  locationChanged: boolean;
  condition?: string;
  conditionNotes?: string;
  conditionChanged: boolean;
  issues?: string[];
  notes?: string;
  photoUrls?: string[];
  photoCount: number;
  auditedBy?: string;
  auditedDate?: string;
  lastModifiedDate?: string;
  offlineId?: string;
  syncedDate?: string;
}

// Frontend AuditItem type for asset progress tracking
export interface AuditItem {
  id: string;
  auditId: string;
  assetCode: string;
  assetName: string;
  status: string;
  isVerified: boolean;
  isFlagged: boolean;
  isMissing: boolean;
  expectedLocation?: string;
  actualLocation?: string;
  locationVerified: boolean;
  locationChanged: boolean;
  condition?: string;
  conditionNotes?: string;
  conditionChanged: boolean;
  issues?: string[];
  notes?: string;
  photoUrls?: string[];
  photoCount: number;
  auditedBy?: string;
  auditedDate?: string;
  lastModifiedDate?: string;
}

// ==================== Mapping Functions ====================

// Map backend status string to frontend AuditStatus enum
const mapBackendStatusToFrontend = (backendStatus: string): AuditStatus => {
  const statusMap: Record<string, AuditStatus> = {
    'draft': AuditStatus.Draft,
    'assigned': AuditStatus.Assigned,
    'in_progress': AuditStatus.InProgress,
    'in-progress': AuditStatus.InProgress,
    'inprogress': AuditStatus.InProgress,
    'under_review': AuditStatus.UnderReview,
    'under-review': AuditStatus.UnderReview,
    'underreview': AuditStatus.UnderReview,
    'pending_approval': AuditStatus.UnderReview,
    'pending-approval': AuditStatus.UnderReview,
    'on_hold': AuditStatus.OnHold,
    'on-hold': AuditStatus.OnHold,
    'onhold': AuditStatus.OnHold,
    'completed': AuditStatus.Completed,
    'cancelled': AuditStatus.Cancelled,
    'canceled': AuditStatus.Cancelled,
    'scheduled': AuditStatus.Scheduled,
  };
  return statusMap[backendStatus?.toLowerCase()] || AuditStatus.Draft;
};

// Map frontend AuditStatus enum to backend status string
const mapFrontendStatusToBackend = (frontendStatus: AuditStatus): string => {
  const statusMap: Record<AuditStatus, string> = {
    [AuditStatus.Draft]: 'draft',
    [AuditStatus.Assigned]: 'assigned',
    [AuditStatus.InProgress]: 'in-progress',
    [AuditStatus.UnderReview]: 'pending-approval',
    [AuditStatus.OnHold]: 'on-hold',
    [AuditStatus.Completed]: 'completed',
    [AuditStatus.Cancelled]: 'cancelled',
    [AuditStatus.Scheduled]: 'scheduled',
  };
  return statusMap[frontendStatus] ?? 'draft';
};

// Map backend scope type to frontend AuditScope enum
const mapBackendScopeToFrontend = (backendScope: string): AuditScope => {
  const scopeMap: Record<string, AuditScope> = {
    'all-assets': AuditScope.AllAssets,
    'all_assets': AuditScope.AllAssets,
    'allassets': AuditScope.AllAssets,
    'locations': AuditScope.Locations,
    'location': AuditScope.Locations,
    'departments': AuditScope.Departments,
    'department': AuditScope.Departments,
    'categories': AuditScope.Categories,
    'category': AuditScope.Categories,
    'custom-list': AuditScope.CustomList,
    'custom_list': AuditScope.CustomList,
    'customlist': AuditScope.CustomList,
    'custom': AuditScope.CustomList,
  };
  return scopeMap[backendScope?.toLowerCase()] || AuditScope.AllAssets;
};

// Map frontend AuditScope enum to backend scope type string
const mapFrontendScopeToBackend = (frontendScope: AuditScope | string | undefined): string => {
  if (!frontendScope) return 'all-assets';
  
  const scopeMap: Record<string, string> = {
    [AuditScope.AllAssets]: 'all-assets',
    [AuditScope.Locations]: 'locations',
    [AuditScope.Departments]: 'departments',
    [AuditScope.Categories]: 'categories',
    [AuditScope.CustomList]: 'custom-list',
  };
  return scopeMap[frontendScope] ?? 'all-assets';
};

// Map backend type to frontend AuditType enum
const mapBackendTypeToFrontend = (backendType: string): AuditType => {
  const typeMap: Record<string, AuditType> = {
    'full-inventory': AuditType.FullInventory,
    'full_inventory': AuditType.FullInventory,
    'fullinventory': AuditType.FullInventory,
    'location-verification': AuditType.LocationVerification,
    'location_verification': AuditType.LocationVerification,
    'locationverification': AuditType.LocationVerification,
    'condition-assessment': AuditType.ConditionAssessment,
    'condition_assessment': AuditType.ConditionAssessment,
    'conditionassessment': AuditType.ConditionAssessment,
    'compliance-check': AuditType.ComplianceCheck,
    'compliance_check': AuditType.ComplianceCheck,
    'compliancecheck': AuditType.ComplianceCheck,
    'high-value-assets': AuditType.HighValueAssets,
    'high_value_assets': AuditType.HighValueAssets,
    'highvalueassets': AuditType.HighValueAssets,
    'reconciliation': AuditType.Reconciliation,
    'spot-check': AuditType.SpotCheck,
    'spot_check': AuditType.SpotCheck,
    'spotcheck': AuditType.SpotCheck,
    'physical': AuditType.Physical,
    'compliance': AuditType.Compliance,
    'financial': AuditType.Financial,
    'condition': AuditType.Condition,
    'verification': AuditType.Verification,
    'one-time': AuditType.FullInventory, // Default for one-time audits
  };
  return typeMap[backendType?.toLowerCase()] || AuditType.FullInventory;
};

// Map frontend AuditType enum to backend type string
const mapFrontendTypeToBackend = (frontendType: AuditType): string => {
  const typeMap: Record<AuditType, string> = {
    [AuditType.FullInventory]: 'full-inventory',
    [AuditType.LocationVerification]: 'location-verification',
    [AuditType.ConditionAssessment]: 'condition-assessment',
    [AuditType.ComplianceCheck]: 'compliance-check',
    [AuditType.HighValueAssets]: 'high-value-assets',
    [AuditType.Reconciliation]: 'reconciliation',
    [AuditType.SpotCheck]: 'spot-check',
    [AuditType.Physical]: 'physical',
    [AuditType.Compliance]: 'compliance',
    [AuditType.Financial]: 'financial',
    [AuditType.Condition]: 'condition',
    [AuditType.Verification]: 'verification',
  };
  return typeMap[frontendType] ?? 'full-inventory';
};

// Map backend priority to frontend AuditPriority enum
const mapBackendPriorityToFrontend = (backendPriority: string | undefined): AuditPriority => {
  const priorityMap: Record<string, AuditPriority> = {
    'critical': AuditPriority.Critical,
    'high': AuditPriority.High,
    'medium': AuditPriority.Medium,
    'low': AuditPriority.Low,
  };
  return priorityMap[backendPriority?.toLowerCase() ?? ''] || AuditPriority.Medium;
};

// Map frontend AuditPriority enum to backend priority string
const mapFrontendPriorityToBackend = (frontendPriority: AuditPriority | undefined): string => {
  if (!frontendPriority) return 'medium';
  return frontendPriority.toLowerCase();
};

// Map backend AssetAuditDto to frontend Audit type
const mapBackendAuditToFrontend = (backendAudit: BackendAssetAuditDto): Audit => {
  // Create assignedUsers array from single assignee
  const assignedUsers: AuditAssignee[] = [];
  if (backendAudit.assignedToUserId || backendAudit.assignedToUserName) {
    assignedUsers.push({
      userId: backendAudit.assignedToUserId?.toString() ?? '',
      userName: backendAudit.assignedToUserName ?? '',
      userEmail: '',
      assignedDate: backendAudit.assignedDate ?? new Date().toISOString(),
      status: AssigneeStatus.Pending,
      completedAssets: backendAudit.assetsAudited ?? 0,
      notificationSent: false,
    });
  }

  return {
    id: backendAudit.id.toString(),
    auditNumber: backendAudit.auditId || `AUD-${backendAudit.id}`,
    title: backendAudit.name ?? '',
    name: backendAudit.name ?? '',
    description: backendAudit.description,
    type: mapBackendTypeToFrontend(backendAudit.type),
    status: mapBackendStatusToFrontend(backendAudit.status),
    priority: mapBackendPriorityToFrontend(backendAudit.priority),
    
    // Scheduling - map backend startDate/dueDate to frontend scheduledStartDate/scheduledEndDate
    scheduledStartDate: backendAudit.startDate,
    scheduledEndDate: backendAudit.dueDate,
    startDate: backendAudit.startDate,
    dueDate: backendAudit.dueDate,
    actualStartDate: undefined,
    actualEndDate: backendAudit.completedDate,
    
    // Scope
    scope: mapBackendScopeToFrontend(backendAudit.scopeType),
    scopeDetails: {
      locationIds: backendAudit.locationIds?.map(id => id.toString()) ?? [],
      categoryIds: backendAudit.categoryIds?.map(id => id.toString()) ?? [],
      assetIds: backendAudit.assetIds ?? [],
    },
    
    // Assignment
    assignedUsers,
    assignees: assignedUsers,
    
    // Progress
    totalAssets: backendAudit.totalAssets ?? 0,
    completedAssets: backendAudit.assetsAudited ?? 0,
    pendingAssets: (backendAudit.totalAssets ?? 0) - (backendAudit.assetsAudited ?? 0),
    exceptionCount: backendAudit.assetsFlagged ?? 0,
    completionPercentage: backendAudit.progressPercentage ?? 0,
    flaggedAssets: backendAudit.assetsFlagged,
    missingAssets: backendAudit.assetsMissing,
    
    // Recurrence
    isRecurring: backendAudit.isRecurring ?? false,
    recurrencePattern: backendAudit.isRecurring ? {
      frequency: backendAudit.recurrenceFrequency as any ?? 'Monthly',
      interval: 1,
      dayOfWeek: backendAudit.recurrenceDaysOfWeek?.[0],
      dayOfMonth: backendAudit.recurrenceDayOfMonth,
      endDate: backendAudit.recurrenceEndDate,
      occurrences: backendAudit.recurrenceEndOccurrences,
    } : undefined,
    parentAuditId: backendAudit.parentRecurringAuditId?.toString(),
    
    // Metadata
    createdBy: backendAudit.createdBy ?? '',
    createdByName: backendAudit.createdBy ?? '',
    createdDate: backendAudit.createdDate ?? new Date().toISOString(),
    lastModifiedBy: backendAudit.modifiedBy,
    lastModifiedDate: backendAudit.modifiedDate,
    
    // Calculated fields
    isOverdue: backendAudit.dueDate ? new Date(backendAudit.dueDate) < new Date() : false,
    
    // Settings
    allowPartialCompletion: true,
    requirePhotos: backendAudit.requirePhotos ?? false,
    requireNotes: false,
    autoResolveExceptions: backendAudit.autoApprove ?? false,
  };
};

// Map frontend Partial<Audit> to backend CreateAuditRequest
const mapFrontendAuditToBackendCreate = (frontendAudit: Partial<Audit>): BackendCreateAuditRequest => {
  // Parse location IDs to integers
  const locationIds = frontendAudit.scopeDetails?.locationIds
    ?.map(id => parseInt(id, 10))
    .filter(id => !isNaN(id));
  
  // Parse category IDs to integers
  const categoryIds = frontendAudit.scopeDetails?.categoryIds
    ?.map(id => parseInt(id, 10))
    .filter(id => !isNaN(id));

  // Get first assignee for single-user assignment
  const firstAssignee = frontendAudit.assignedUsers?.[0];
  const assignedToUserId = firstAssignee?.userId ? parseInt(firstAssignee.userId, 10) : undefined;

  return {
    Name: frontendAudit.title ?? frontendAudit.name ?? '',
    Description: frontendAudit.description,
    Type: frontendAudit.type ? mapFrontendTypeToBackend(frontendAudit.type) : 'full-inventory',
    ScopeType: mapFrontendScopeToBackend(frontendAudit.scope),
    LocationIds: locationIds && locationIds.length > 0 ? locationIds : undefined,
    CategoryIds: categoryIds && categoryIds.length > 0 ? categoryIds : undefined,
    AssetIds: frontendAudit.scopeDetails?.assetIds,
    AssignedToUserId: !isNaN(assignedToUserId || NaN) ? assignedToUserId : undefined,
    AssignedToUserName: firstAssignee?.userName ?? '',
    StartDate: frontendAudit.scheduledStartDate,
    DueDate: frontendAudit.scheduledEndDate,
    Priority: frontendAudit.priority ? mapFrontendPriorityToBackend(frontendAudit.priority) : 'medium',
    IsRecurring: frontendAudit.isRecurring ?? false,
    RequirePhotos: frontendAudit.requirePhotos ?? false,
  };
};

// Map backend AssetAuditResultDto to frontend AuditItem
const mapBackendResultToFrontendItem = (backendResult: BackendAssetAuditResultDto): AuditItem => {
  return {
    id: backendResult.id.toString(),
    auditId: backendResult.auditId.toString(),
    assetCode: backendResult.assetCode ?? '',
    assetName: backendResult.assetName ?? '',
    status: backendResult.status ?? 'pending',
    isVerified: backendResult.isVerified ?? false,
    isFlagged: backendResult.isFlagged ?? false,
    isMissing: backendResult.isMissing ?? false,
    expectedLocation: backendResult.expectedLocationName,
    actualLocation: backendResult.actualLocationName,
    locationVerified: backendResult.locationVerified ?? false,
    locationChanged: backendResult.locationChanged ?? false,
    condition: backendResult.condition,
    conditionNotes: backendResult.conditionNotes,
    conditionChanged: backendResult.conditionChanged ?? false,
    issues: backendResult.issues,
    notes: backendResult.notes,
    photoUrls: backendResult.photoUrls,
    photoCount: backendResult.photoCount ?? 0,
    auditedBy: backendResult.auditedBy,
    auditedDate: backendResult.auditedDate,
    lastModifiedDate: backendResult.lastModifiedDate,
  };
};

// Helper function to get current data source mode from feature settings
const getDataSource = (): 'demo' | 'api' => {
  try {
    const featureSettings = localStorage.getItem('feature-settings');
    if (featureSettings) {
      const parsed = JSON.parse(featureSettings);
      const auditsModule = parsed.modules?.audits;
      if (auditsModule) {
        const mode = auditsModule.mode === 'live' ? 'api' : 'demo';
        console.log('[audit-data-service] getDataSource - audits module mode:', auditsModule.mode, '-> returning:', mode);
        return mode;
      }
    }
    console.log('[audit-data-service] getDataSource - no feature settings found, defaulting to API mode');
  } catch (error) {
    console.error('[audit-data-service] Error reading feature settings:', error);
  }
  // Default to API mode (live) if feature settings not found - audits module defaults to live
  console.log('[audit-data-service] getDataSource - returning default: api');
  return 'api';
};

class AuditDataService {
  private authHeader?: string;

  private getApiBaseUrl(): string {
    const baseUrl = import.meta.env.VITE_API_BASE_URL || getBackendUrl();

    try {
      const url = new URL(baseUrl);
      if (url.username || url.password) {
        const credentials = btoa(`${url.username}:${url.password}`);
        this.authHeader = `Basic ${credentials}`;
        url.username = '';
        url.password = '';
        return url.toString().replace(/\/$/, '');
      }
      return baseUrl.replace(/\/$/, '');
    } catch (e) {
      return baseUrl.replace(/\/$/, '');
    }
  }

  private async fetchAPI<T>(endpoint: string, options?: RequestInit): Promise<T> {
    const apiBaseUrl = this.getApiBaseUrl();

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options?.headers as Record<string, string>),
    };

    if (this.authHeader) {
      headers['Authorization'] = this.authHeader;
    }

    // Use extracted helper function to reduce cognitive complexity (S3776)
    const token = await getAuditBearerToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    // Use the same default as the backend seed data
    const authConfig = getAuthConfig();
    const instanceId = authConfig.instanceId ?? '00000000-0000-0000-0000-000000000000';
    console.log('[audit-data-service] getHeaders - Setting X-Instance-Id:', instanceId, 'from authConfig.instanceId:', authConfig.instanceId);
    headers['X-Instance-Id'] = instanceId;

    try {
      const response = await fetch(`${apiBaseUrl}${endpoint}`, {
        ...options,
        headers,
      });

      if (!response.ok) {
        // Use extracted helper function to reduce cognitive complexity (S3776)
        const errorMessage = await parseAuditApiErrorResponse(response);
        throw new Error(errorMessage);
      }

      if (response.status === 204) {
        return {} as T;
      }

      return response.json();
    } catch (error) {
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error(
          `Unable to connect to API at ${apiBaseUrl}. Please ensure the backend server is running or switch to Demo mode.`
        );
      }
      throw error;
    }
  }

  // ==================== Dashboard ====================

  async getDashboardMetrics(): Promise<DashboardMetrics> {
    if (getDataSource() === 'demo') {
      return mockApiService.getDashboardMetrics();
    }
    return this.fetchAPI<DashboardMetrics>('/api/Audits/dashboard/metrics');
  }

  // ==================== Audits ====================

  async getAudits(params?: {
    statuses?: AuditStatus[];
    search?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    page?: number;
    pageSize?: number;
    isOverdue?: boolean;
    priorities?: string[];
    types?: string[];
    assigneeIds?: string[];
  }): Promise<PagedResult<Audit>> {
    console.log('[audit-data-service] getAudits called with params:', params);
    const dataSource = getDataSource();
    console.log('[audit-data-service] getAudits - dataSource:', dataSource);
    
    if (dataSource === 'demo') {
      console.log('[audit-data-service] getAudits - Using DEMO data');
      return mockApiService.getAudits(params);
    }
    
    console.log('[audit-data-service] getAudits - Using API data');
    console.log('[audit-data-service] getAudits - Backend URL:', this.getApiBaseUrl());
    console.log('[audit-data-service] getAudits - Input params:', JSON.stringify(params, null, 2));

    // Build query params for API
    const queryParams = new URLSearchParams();
    console.log('[audit-data-service] Building query params...');
    
    // Send each status as a separate query param
    // The backend should handle multiple status parameters (e.g., ?status=draft&status=in_progress)
    if (params?.statuses && params.statuses.length > 0) {
      console.log('[audit-data-service] Frontend statuses:', params.statuses);
      // Map frontend status enums to backend status strings
      const backendStatuses = params.statuses.map(s => mapFrontendStatusToBackend(s));
      console.log('[audit-data-service] Backend statuses:', backendStatuses);
      // Send each status as a separate parameter
      backendStatuses.forEach(status => {
        queryParams.append('status', status);
      });
    }
    if (params?.search) {
      queryParams.append('search', params.search);
    }
    if (params?.page) {
      queryParams.append('page', params.page.toString());
    }
    if (params?.pageSize) {
      queryParams.append('pageSize', params.pageSize.toString());
    }
    if (params?.priorities && params.priorities.length > 0) {
      queryParams.append('priority', params.priorities[0].toLowerCase());
    }
    if (params?.types && params.types.length > 0) {
      queryParams.append('type', params.types[0].toLowerCase());
    }

    const queryString = queryParams.toString();
    const endpoint = '/api/Audits' + (queryString ? '?' + queryString : '');
    
    console.log('[audit-data-service] getAudits - Data source:', getDataSource());
    console.log('[audit-data-service] getAudits - Query string:', queryString);
    console.log('[audit-data-service] getAudits - Fetching from:', `${this.getApiBaseUrl()}${endpoint}`);
    console.log('[audit-data-service] getAudits - Query params:', params);

    // The backend returns array with headers, we need to transform to PagedResult
    const response = await fetch(`${this.getApiBaseUrl()}${endpoint}`, {
      headers: await this.getHeaders(),
    });

    console.log('[audit-data-service] getAudits - Response status:', response.status);
    console.log('[audit-data-service] getAudits - Response headers:', {
      totalCount: response.headers.get('X-Total-Count'),
      page: response.headers.get('X-Page'),
      pageSize: response.headers.get('X-Page-Size'),
    });

    if (!response.ok) {
      throw new Error(`API error: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    console.log('[audit-data-service] getAudits - Raw data count:', data.length);
    console.log('[audit-data-service] getAudits - First item:', data[0]);
    
        const totalCount = parseInt(response.headers.get('X-Total-Count') ?? '0', 10);
        const page = parseInt(response.headers.get('X-Page') ?? '1', 10);
        const pageSize = parseInt(response.headers.get('X-Page-Size') ?? '20', 10);
    const totalPages = Math.ceil(totalCount / pageSize);

    // Map backend DTOs to frontend Audit type
    console.log('[audit-data-service] getAudits - Starting mapping...');
    const mappedItems: Audit[] = [];
    try {
      for (let i = 0; i < data.length; i++) {
        console.log(`[audit-data-service] Mapping item ${i + 1}/${data.length}:`, data[i].auditId);
        const mapped = mapBackendAuditToFrontend(data[i]);
        mappedItems.push(mapped);
      }
    } catch (error) {
      console.error('[audit-data-service] Error mapping audits:', error);
      throw error;
    }
    
    console.log('[audit-data-service] getAudits - Mapped items count:', mappedItems.length);
    console.log('[audit-data-service] getAudits - First mapped item:', mappedItems[0]);
    console.log('[audit-data-service] getAudits - Returning result:', {
      itemsCount: mappedItems.length,
      page,
      pageSize,
      totalCount,
      totalPages,
    });

    return {
      items: mappedItems,
      page,
      pageSize,
      totalCount: totalCount,
      totalPages,
      hasNextPage: page < totalPages,
      hasPreviousPage: page > 1,
    };
  }

  private async getHeaders(): Promise<Record<string, string>> {
    console.log('[audit-data-service] getHeaders CALLED');
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (this.authHeader) {
      headers['Authorization'] = this.authHeader;
    }

    const authConfig = getAuthConfig();
    console.log('[audit-data-service] getHeaders - authConfig:', JSON.stringify({ instanceId: authConfig.instanceId, hasGetAccessToken: !!authConfig.getAccessToken }));

    if (authConfig.getAccessToken) {
      try {
        const token = await authConfig.getAccessToken();
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
      } catch (error) {
        console.error('Error getting access token:', error);
      }
    }

    // Use the same default as the backend seed data
    const instanceId = authConfig.instanceId ?? '00000000-0000-0000-0000-000000000000';
    console.log('[audit-data-service] getHeaders - Setting X-Instance-Id:', instanceId, 'from authConfig.instanceId:', authConfig.instanceId);
    headers['X-Instance-Id'] = instanceId;

    return headers;
  }

  async getAuditById(id: string): Promise<Audit | null> {
    if (getDataSource() === 'demo') {
      return mockApiService.getAuditById(id);
    }
    try {
      const backendAudit = await this.fetchAPI<BackendAssetAuditDto>(`/api/Audits/${id}`);
      return mapBackendAuditToFrontend(backendAudit);
    } catch (error) {
      console.error('Error fetching audit:', error);
      return null;
    }
  }

  async createAudit(audit: Partial<Audit>): Promise<Audit> {
    if (getDataSource() === 'demo') {
      return mockApiService.createAudit(audit);
    }
    // Map frontend Audit to backend CreateAuditRequest format
    const backendRequest = mapFrontendAuditToBackendCreate(audit);
    const backendResponse = await this.fetchAPI<BackendAssetAuditDto>('/api/Audits', {
      method: 'POST',
      body: JSON.stringify(backendRequest),
    });
    // Map backend response back to frontend Audit type
    return mapBackendAuditToFrontend(backendResponse);
  }

  // ==================== Audit Items (Asset Progress) ====================

  async getAuditItems(
    auditId: string,
    page: number = 1,
    pageSize: number = 50
  ): Promise<PagedResult<AuditItem>> {
    if (getDataSource() === 'demo') {
      // In demo mode, return mock audit items
      return mockApiService.getAuditItems(auditId, page, pageSize);
    }

    // Build query params
    const queryParams = new URLSearchParams();
    queryParams.append('page', page.toString());
    queryParams.append('pageSize', pageSize.toString());

    const queryString = queryParams.toString();
    const endpoint = `/api/Audits/${auditId}/results` + (queryString ? '?' + queryString : '');

    const response = await fetch(`${this.getApiBaseUrl()}${endpoint}`, {
      headers: await this.getHeaders(),
    });

    if (!response.ok) {
      throw new Error(`API error: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    
    // Handle both array response and paged response from backend
    let items: BackendAssetAuditResultDto[];
    let totalCount: number;
    
    if (Array.isArray(data)) {
      items = data;
      totalCount = parseInt(response.headers.get('X-Total-Count') || String(data.length), 10);
    } else if (data.items) {
      items = data.items;
      totalCount = data.totalCount || data.items.length;
    } else {
      items = [];
      totalCount = 0;
    }

    const totalPages = Math.ceil(totalCount / pageSize);

    return {
      items: items.map(mapBackendResultToFrontendItem),
      page,
      pageSize,
      totalCount,
      totalPages,
      hasNextPage: page < totalPages,
      hasPreviousPage: page > 1,
    };
  }

  async updateAudit(id: string, updates: Partial<Audit>): Promise<Audit> {
    if (getDataSource() === 'demo') {
      return mockApiService.updateAudit(id, updates);
    }
    await this.fetchAPI<void>(`/api/Audits/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
    // Fetch and return the updated audit
    const updated = await this.getAuditById(id);
    if (!updated) {
      throw new Error('Failed to fetch updated audit');
    }
    return updated;
  }

  async deleteAudit(id: string): Promise<void> {
    if (getDataSource() === 'demo') {
      return mockApiService.deleteAudit(id);
    }
    await this.fetchAPI<void>(`/api/Audits/${id}`, {
      method: 'DELETE',
    });
  }

  async startAudit(id: string): Promise<void> {
    if (getDataSource() === 'demo') {
      await mockApiService.updateAudit(id, { status: AuditStatus.InProgress });
      return;
    }
    await this.fetchAPI<void>(`/api/Audits/${id}/start`, {
      method: 'POST',
    });
  }

  async completeAudit(id: string): Promise<void> {
    if (getDataSource() === 'demo') {
      await mockApiService.updateAudit(id, { status: AuditStatus.Completed });
      return;
    }
    await this.fetchAPI<void>(`/api/Audits/${id}/complete`, {
      method: 'POST',
    });
  }

  async submitForApproval(id: string): Promise<void> {
    if (getDataSource() === 'demo') {
      await mockApiService.updateAudit(id, { status: AuditStatus.UnderReview });
      return;
    }
    await this.fetchAPI<void>(`/api/Audits/${id}/submit-for-approval`, {
      method: 'POST',
    });
  }

  async approveAudit(id: string, comment?: string): Promise<void> {
    if (getDataSource() === 'demo') {
      await mockApiService.updateAudit(id, { status: AuditStatus.Completed });
      return;
    }
    await this.fetchAPI<void>(`/api/Audits/${id}/approve`, {
      method: 'POST',
      body: JSON.stringify({ comment }),
    });
  }

  async rejectAudit(id: string, reason: string): Promise<void> {
    if (getDataSource() === 'demo') {
      await mockApiService.updateAudit(id, { status: AuditStatus.InProgress });
      return;
    }
    await this.fetchAPI<void>(`/api/Audits/${id}/reject`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  }

  async updateAuditStatus(id: string, status: AuditStatus): Promise<void> {
    if (getDataSource() === 'demo') {
      await mockApiService.updateAudit(id, { status });
      return;
    }
    
    // Map status to appropriate backend endpoint
    if (status === AuditStatus.InProgress) {
      await this.fetchAPI<void>(`/api/Audits/${id}/start`, {
        method: 'POST',
      });
    } else if (status === AuditStatus.Completed) {
      await this.fetchAPI<void>(`/api/Audits/${id}/complete`, {
        method: 'POST',
      });
    } else if (status === AuditStatus.Cancelled) {
      await this.fetchAPI<void>(`/api/Audits/${id}/cancel`, {
        method: 'POST',
      });
    } else {
      // For other statuses, use a generic update endpoint if available
      // Otherwise, we'll need to add specific endpoints in the backend
      throw new Error(`Status update to ${status} not yet implemented`);
    }
  }

  async reopenAudit(id: string): Promise<void> {
    if (getDataSource() === 'demo') {
      await mockApiService.updateAudit(id, { status: AuditStatus.InProgress });
      return;
    }
    await this.fetchAPI<void>(`/api/Audits/${id}/reopen`, {
      method: 'POST',
    });
  }

  // ==================== Scheduled Audits ====================

  async getScheduledAudits(params?: {
    search?: string;
    page?: number;
    pageSize?: number;
  }): Promise<PagedResult<Audit>> {
    // Get audits with Scheduled status
    return this.getAudits({
      statuses: [AuditStatus.Scheduled],
      search: params?.search,
      page: params?.page,
      pageSize: params?.pageSize,
    });
  }

  // ==================== Exceptions ====================

  async getExceptions(params?: {
    pageSize?: number;
    page?: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    statuses?: string[];
    severities?: string[];
    types?: string[];
    auditIds?: string[];
    search?: string;
  }): Promise<PagedResult<AuditException>> {
    if (getDataSource() === 'demo') {
      return mockApiService.getExceptions(params);
    }

    // Build query params for API
    const queryParams = new URLSearchParams();
    if (params?.statuses && params.statuses.length > 0) {
      queryParams.append('status', params.statuses.join(','));
    }
    if (params?.severities && params.severities.length > 0) {
      queryParams.append('severity', params.severities.join(','));
    }
    if (params?.types && params.types.length > 0) {
      queryParams.append('exceptionType', params.types.join(','));
    }
    if (params?.auditIds && params.auditIds.length > 0) {
      queryParams.append('auditId', params.auditIds[0]); // API takes single auditId
    }
    if (params?.search) {
      queryParams.append('search', params.search);
    }
    if (params?.page) {
      queryParams.append('page', params.page.toString());
    }
    if (params?.pageSize) {
      queryParams.append('pageSize', params.pageSize.toString());
    }
    if (params?.sortBy) {
      queryParams.append('sortBy', params.sortBy);
    }
    if (params?.sortOrder) {
      queryParams.append('sortOrder', params.sortOrder);
    }

    const queryString = queryParams.toString();
    const endpoint = `/api/AuditExceptions${queryString ? `?${queryString}` : ''}`;

    const result = await this.fetchAPI<{
      items: AuditException[];
      totalCount: number;
      page: number;
      pageSize: number;
      totalPages: number;
      hasNextPage: boolean;
      hasPreviousPage: boolean;
    }>(endpoint);

    // Backend response already matches frontend PagedResult interface
    return result;
  }

  async getExceptionById(id: string): Promise<AuditException | null> {
    if (getDataSource() === 'demo') {
      return mockApiService.getExceptionById(id);
    }
    try {
      return await this.fetchAPI<AuditException>(`/api/AuditExceptions/${id}`);
    } catch (error) {
      console.error('Error fetching exception:', error);
      return null;
    }
  }

  async resolveException(
    id: string,
    resolution: { notes: string; resolvedBy: string; action?: string }
  ): Promise<AuditException> {
    if (getDataSource() === 'demo') {
      return mockApiService.resolveException(id, resolution);
    }
    await this.fetchAPI<void>(`/api/AuditExceptions/${id}/resolve`, {
      method: 'POST',
      body: JSON.stringify({
        resolutionNotes: resolution.notes,
        resolvedBy: resolution.resolvedBy,
        resolutionAction: resolution.action,
      }),
    });
    // Fetch and return the updated exception
    const updated = await this.getExceptionById(id);
    if (!updated) {
      throw new Error('Failed to fetch updated exception');
    }
    return updated;
  }

  async dismissException(
    id: string,
    dismissal: { reason: string; dismissedBy: string }
  ): Promise<void> {
    if (getDataSource() === 'demo') {
      // In demo mode, just resolve with the reason
      await mockApiService.resolveException(id, {
        notes: `Dismissed: ${dismissal.reason}`,
        resolvedBy: dismissal.dismissedBy,
      });
      return;
    }
    await this.fetchAPI<void>(`/api/AuditExceptions/${id}/dismiss`, {
      method: 'POST',
      body: JSON.stringify({
        reason: dismissal.reason,
        dismissedBy: dismissal.dismissedBy,
      }),
    });
  }

  async getExceptionsByAudit(auditId: string): Promise<AuditException[]> {
    if (getDataSource() === 'demo') {
      const result = await mockApiService.getExceptions({ auditIds: [auditId] });
      return result.items;
    }
    return this.fetchAPI<AuditException[]>(`/api/AuditExceptions/by-audit/${auditId}`);
  }

  async updateException(
    id: string,
    updates: Partial<{
      status: string;
      severity: string;
      priority: string;
      assignedTo: string;
      notes: string;
    }>
  ): Promise<AuditException> {
    if (getDataSource() === 'demo') {
      const exception = await mockApiService.getExceptionById(id);
      if (!exception) {
        throw new Error('Exception not found');
      }
      return { ...exception, ...updates } as AuditException;
    }
    return this.fetchAPI<AuditException>(`/api/AuditExceptions/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  // ==================== Activity Logs ====================

  async getActivityLogs(
    auditId: string,
    page: number = 1,
    pageSize: number = 50
  ): Promise<PagedResult<AuditActivityLog>> {
    if (getDataSource() === 'demo') {
      return mockApiService.getActivityLogs(auditId, page, pageSize);
    }
    const events = await this.fetchAPI<AuditActivityLog[]>(`/api/Audits/${auditId}/events`);
    // Transform to PagedResult
    const totalItems = events.length;
    const totalPages = Math.ceil(totalItems / pageSize);
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;

    return {
      items: events.slice(startIndex, endIndex),
      page,
      pageSize,
      totalCount: totalItems,
      totalPages,
      hasNextPage: page < totalPages,
      hasPreviousPage: page > 1,
    };
  }

  // ==================== Users ====================

  async getUsers(): Promise<{ id: string; name: string; email?: string }[]> {
    if (getDataSource() === 'demo') {
      const users = await mockApiService.getUsers();
      return users.map((u) => ({ id: u.id, name: u.name, email: u.email }));
    }
    // TODO: Implement real API endpoint for users
    // For now, return empty array in live mode
    return [];
  }

  // ==================== Stats ====================

  async getAuditStats(): Promise<{
    totalAudits: number;
    activeAudits: number;
    completedAudits: number;
    pendingApproval: number;
    overdueAudits: number;
  }> {
    if (getDataSource() === 'demo') {
      const metrics = await mockApiService.getDashboardMetrics();
      return {
        totalAudits: metrics.activeAuditsCount + metrics.overdueAuditsCount,
        activeAudits: metrics.activeAuditsCount,
        completedAudits: 0, // Not available in demo metrics
        pendingApproval: metrics.activeAuditsBreakdown?.underReview ?? 0,
        overdueAudits: metrics.overdueAuditsCount,
      };
    }
    return this.fetchAPI<{
      totalAudits: number;
      activeAudits: number;
      completedAudits: number;
      pendingApproval: number;
      overdueAudits: number;
    }>('/api/Audits/stats');
  }

  async getExceptionStats(): Promise<{
    totalExceptions: number;
    openExceptions: number;
    resolvedExceptions: number;
    criticalExceptions: number;
    highSeverityExceptions: number;
  }> {
    if (getDataSource() === 'demo') {
      const metrics = await mockApiService.getDashboardMetrics();
      return {
        totalExceptions: metrics.openExceptionsCount,
        openExceptions: metrics.openExceptionsCount,
        resolvedExceptions: 0,
        criticalExceptions: metrics.openExceptionsBySeverity?.critical ?? 0,
        highSeverityExceptions: metrics.openExceptionsBySeverity?.high ?? 0,
      };
    }
    return this.fetchAPI<{
      totalExceptions: number;
      openExceptions: number;
      resolvedExceptions: number;
      criticalExceptions: number;
      highSeverityExceptions: number;
    }>('/api/AuditExceptions/stats');
  }
}

// Export singleton instance
export const auditDataService = new AuditDataService();
